import gql from "graphql-tag";
import { useMutation as defaultUseMutation, useQuery as defaultUseQuery, } from "@apollo/react-hooks";
import { useCurrentAccountId, useCurrentPlan, } from "@zapier/promotions/esm/hooks";
import { isOfferExpired } from "@zapier/promotions/esm/utils";
import { getOfferEndDate } from "./getOfferEndDate";
const offerVariantFragment = gql `
  fragment offerVariantFragment on OfferVariant {
    id
    campaign {
      id
      maxUses
      name
      slug
    }
    destinationPlan {
      amount
      displayShortName
      id
      interval
      name
    }
    promotionPlanGroup {
      id
      name
    }
    isControl
    monthlyCost
    name
    terms
    totalCost
  }
`;
const offerQuery = gql `
  query offerQuery($accountId: Int!, $offerId: ID!) {
    account(id: $accountId) {
      id
      activeOfferId
      offer(offerId: $offerId) {
        id
        acceptedAt
        accountId
        createdAt
        expiry
        variant {
          ...offerVariantFragment
        }
      }
      promotionPlanGroupId
    }
  }
  ${offerVariantFragment}
`;
const acceptOfferMutation = gql `
  mutation acceptOffer($input: AcceptOfferInput!) {
    acceptOffer(input: $input) {
      acceptedAt
      targetPlanId
      variant {
        ...offerVariantFragment
      }
    }
  }
  ${offerVariantFragment}
`;
function omitTypename(key, value) {
    return key === "__typename" ? undefined : value;
}
const defaultProps = {
    onAcceptOfferSuccess: () => {
    },
};
export const useOffer = ({ offerId, onAcceptOfferSuccess, ...props } = defaultProps) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const useQuery = props.useQuery || defaultUseQuery;
    const useMutation = props.useMutation || defaultUseMutation;
    const { accountId } = useCurrentAccountId(props);
    const currentPlanData = useCurrentPlan({ useQuery });
    const queryResult = useQuery(offerQuery, {
        skip: !offerId || !accountId,
        variables: {
            accountId,
            offerId,
        },
    });
    const [acceptOffer, mutationResult] = useMutation(acceptOfferMutation, {
        onCompleted: onAcceptOfferSuccess,
    });
    const offer = (_b = (_a = queryResult === null || queryResult === void 0 ? void 0 : queryResult.data) === null || _a === void 0 ? void 0 : _a.account) === null || _b === void 0 ? void 0 : _b.offer;
    const activeOfferId = (_d = (_c = queryResult === null || queryResult === void 0 ? void 0 : queryResult.data) === null || _c === void 0 ? void 0 : _c.account) === null || _d === void 0 ? void 0 : _d.activeOfferId;
    const onAcceptOffer = (targetPlanId) => {
        acceptOffer({
            variables: {
                input: {
                    acceptedAt: new Date(),
                    accountId,
                    offerId: offer === null || offer === void 0 ? void 0 : offer.id,
                    targetPlanId,
                    variant: JSON.parse(JSON.stringify(offer === null || offer === void 0 ? void 0 : offer.variant), omitTypename),
                },
            },
        });
    };
    const offerFoundForAccount = !(queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading) && !!offer;
    const offerAcceptedOrExpired = (offer === null || offer === void 0 ? void 0 : offer.acceptedAt) || isOfferExpired(offer === null || offer === void 0 ? void 0 : offer.expiry);
    const getError = () => {
        if (currentPlanData === null || currentPlanData === void 0 ? void 0 : currentPlanData.error) {
            return "We've run into an issue fetching account data. Please try again.";
        }
        if (mutationResult === null || mutationResult === void 0 ? void 0 : mutationResult.error) {
            return "We've run into an issue applying this offer. Please try again.";
        }
        return undefined;
    };
    return {
        acceptOffer: onAcceptOffer,
        offer,
        offerEndDate: getOfferEndDate({
            acceptedAt: offer === null || offer === void 0 ? void 0 : offer.acceptedAt,
            interval: (_g = (_f = (_e = offer === null || offer === void 0 ? void 0 : offer.variant) === null || _e === void 0 ? void 0 : _e.destinationPlan) === null || _f === void 0 ? void 0 : _f.interval) !== null && _g !== void 0 ? _g : (_h = currentPlanData === null || currentPlanData === void 0 ? void 0 : currentPlanData.currentPlan) === null || _h === void 0 ? void 0 : _h.interval,
            maxUses: (_k = (_j = offer === null || offer === void 0 ? void 0 : offer.variant) === null || _j === void 0 ? void 0 : _j.campaign) === null || _k === void 0 ? void 0 : _k.maxUses,
        }),
        error: getError(),
        isLoading: (queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading) || (currentPlanData === null || currentPlanData === void 0 ? void 0 : currentPlanData.isLoading),
        isOfferActive: Boolean(activeOfferId && activeOfferId === (offer === null || offer === void 0 ? void 0 : offer.id)),
        isOfferAvailable: Boolean(offerFoundForAccount && !offerAcceptedOrExpired),
        isSaving: mutationResult.loading,
    };
};

// @zapier/upsells@5.0.1 (c64b11b3c)
